/**
 * @fileoverview Ops console — activity feed. Renders the append-only platform
 * audit ledger (GET /ops/events) as human-readable sentences:
 * "dan@… turned BILLING_ENABLED on (was off) · Jul 21, 3:12 PM".
 *
 * Flag writes are the whole point of this ledger, so `flag_updated` gets a
 * before → after rendering rather than the generic fallback: knowing a flag
 * changed is much less useful than knowing what it changed from.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import opsApi, { getOpsErrorMessage } from "@/lib/opsApi";
import { Card, Icon } from "@/components/studio/primitives";
import { isFlagOn, displayValue } from "@/lib/opsFlags";

/** Render one side of a flag change — bools read on/off, strings read literally. */
function valueText(value) {
  if (value == null || value === "") return "not set";
  const lowered = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "on", "false", "0", "no", "off"].includes(lowered)) {
    return isFlagOn(value) ? "on" : "off";
  }
  return displayValue(value);
}

function flagSentence(detail) {
  const key = detail?.key || "a flag";
  const before = valueText(detail?.before);
  const after = valueText(detail?.after);
  if (detail?.cleared) {
    return `cleared the override on ${key} — back to the deploy value (${after}, was ${before})`;
  }
  return `set ${key} to ${after} (was ${before})`;
}

/** One sentence per event kind. */
export function describe(event) {
  const actor = event.actor_email || "An operator";
  switch (event.kind) {
    case "login":
      return `${actor} signed in`;
    case "flag_updated":
      return `${actor} ${flagSentence(event.detail)}`;
    case "password_reset":
      return `${actor} reset their password from an emailed link`;
    case "password_changed":
      return `${actor} changed their password`;
    // A founding invitation IS a complimentary year of the top tier, so who
    // issued one — and whether it actually reached anybody — belongs in the
    // change history rather than in a `${actor} — founding_invite_minted`
    // fallback nobody can read.
    case "founding_invite_minted":
      return `${actor} invited ${event.detail?.email || "a teacher"} to the founding cohort${
        event.detail?.emailed === false ? " (link not emailed)" : ""
      }`;
    case "founding_invite_emailed":
      return `${actor} re-sent the founding invitation to ${event.detail?.email || "a teacher"}`;
    case "founding_invite_revoked":
      return `${actor} revoked the founding invitation for ${event.detail?.email || "a teacher"}`;
    default:
      return `${actor} — ${event.kind}`;
  }
}

const ICONS = {
  login: "logout",
  flag_updated: "settings",
  password_reset: "lock",
  password_changed: "lock",
  founding_invite_minted: "star",
  founding_invite_emailed: "star",
  founding_invite_revoked: "star",
};

function when(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function OpsActivityPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await opsApi.get("/ops/events?limit=200");
      setEvents(resp.data.events || []);
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't load activity"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Activity</h1>
      <p className="mb-6 font-sans text-[13px] text-[var(--text-tertiary)]">
        Every ops action, permanently recorded — who signed in, and exactly which flag they moved.
      </p>

      <Card padding={0}>
        {loading ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : events.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">No activity yet.</div>
        ) : (
          events.map((ev, i) => (
            <div
              key={ev.event_id}
              data-testid="ops-event"
              className={[
                "flex items-start gap-3 px-[18px] py-[13px]",
                i < events.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
              ].join(" ")}
            >
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--studio-interactive)]">
                <Icon
                  name={ICONS[ev.kind] || "clock"}
                  size={15}
                  strokeWidth={1.7}
                  className="text-[var(--text-secondary)]"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-sans text-[15px] leading-snug text-[var(--text-primary)]">{describe(ev)}</div>
                <div className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">{when(ev.created_at)}</div>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
